import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'react-use';
import { useLocation } from 'react-router-dom';

import { logInGoogle, getAuthFlowFromPath, SIGNUP_PATH } from 'utils/auth';
import { GoogleLogo } from 'static';
import { Loader } from 'components';

import { useResetOAuthButtonLoadingOnReturn } from '../useResetOAuthButtonLoadingOnReturn';
import { Button } from './styled';

interface GoogleAuthButtonProps {
  disableContinueAsText?: boolean;
  preventDefaultAction?: boolean;
  disabled?: boolean;
  forceLoading?: boolean;
  onAuthStart?: () => void;
  highlighted?: boolean;
  style?: { [key: string]: string };
  iconOnly?: boolean;
}

const GoogleAuthButton: FC<GoogleAuthButtonProps> = ({
  disableContinueAsText,
  preventDefaultAction,
  disabled,
  forceLoading,
  onAuthStart,
  highlighted,
  style,
  iconOnly,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const isMountedRef = useRef(true);
  const [prevAuth] = useLocalStorage<{ [key: string]: string }>(
    'socialSignIns',
    {},
  );
  const buttonText = useMemo(() => {
    let text = `${t('auth.signInWith')} Google`;

    if (
      !disableContinueAsText &&
      location.pathname !== SIGNUP_PATH &&
      prevAuth?.google
    ) {
      text = `${t('auth.continueAs')} ${prevAuth.google}`;
    }

    return text;
  }, [prevAuth, location, t, disableContinueAsText]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useResetOAuthButtonLoadingOnReturn(setIsLoading, isMountedRef);

  const handleClick = () => {
    if (isLoading || forceLoading) return;
    onAuthStart?.();
    setIsLoading(true);
    logInGoogle({
      flow: getAuthFlowFromPath(location.pathname),
    }).catch(() => {
      if (!isMountedRef.current) return;
      setIsLoading(false);
    });
  };

  const effectiveLoading = Boolean(forceLoading || isLoading);

  return (
    <Button
      style={style}
      type="button"
      disabled={disabled || effectiveLoading}
      onClick={preventDefaultAction ? null : handleClick}
      $highlighted={highlighted && !disabled && !effectiveLoading}
      $iconOnly={iconOnly}
    >
      {effectiveLoading ? (
        <Loader type="button" size="20px" thickness={2} />
      ) : (
        <>
          <GoogleLogo />
          {!iconOnly && buttonText}
        </>
      )}
    </Button>
  );
};

export default GoogleAuthButton;
